import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Table as MuiTable,
  MenuItem,
  FormControl,
  Select,
  Pagination,
  Grid,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import Skeleton from "@mui/material/Skeleton";
import ErrorPage from "../Components/ErrorPage";
import PurchaseAdd from "./PurchaseAdd";
import PurchaseEdit from "./PurchaseEdit";
import useDelete from "../../api/useDelete";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { printPurchase } from "../utils/printPurchase";

import { formatJalaliDate } from "../utils/dateFormat";
const belongsToShop = (item, shopId) =>
  !shopId || String(item?.shop ?? item?.shop_details?.id ?? "") === String(shopId);

// Helper to format currency objects like { USD: 140 }
const formatCurrency = (currencyObj) => {
  if (!currencyObj) return "-";
  return Object.entries(currencyObj)
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");
};

// Helper to format date strings (only date)
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return formatJalaliDate(date);
};

const PurchaseRow = ({ purchase, onEdit, onDelete, onPrint, index }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Render nested table for purchase items with an expire date column.
  const renderItemsTable = () => {
    if (purchase.purchase_items && purchase.purchase_items.length > 0) {
      return (
        <MuiTable size="small" aria-label="purchase items">
          <TableHead>
            <TableRow>
              <TableCell>{t("purchase.product")}</TableCell>
              <TableCell>{t("purchase.quantity")}</TableCell>
              <TableCell>{t("purchase.buyPrice")}</TableCell>
              <TableCell>{t("purchase.wholesalePrice", "Wholesale Price")}</TableCell>
              <TableCell>{t("purchase.retailPrice", "Retail Price")}</TableCell>
              <TableCell>{t("purchase.unit")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchase.purchase_items.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  {item.product_details?.name || t("purchase.unknownProduct")}
                </TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>{item.buy_price}</TableCell>
                <TableCell>{item.wholesale_price ?? "-"}</TableCell>
                <TableCell>{item.retail_price ?? "-"}</TableCell>
                <TableCell>{item.unit_details?.title || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      );
    }
    return <Typography variant="body2">{t("purchase.noItems")}</Typography>;
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          {index}
        </TableCell>
        <TableCell>{purchase.shop_details?.name}</TableCell>
        <TableCell>{purchase.supplier_details?.name || "-"}</TableCell>
        <TableCell>{formatDate(purchase.purchase_date)}</TableCell>
        <TableCell>{purchase.amount}</TableCell>
        <TableCell>{formatCurrency(purchase.total_price)}</TableCell>
        <TableCell>{formatCurrency(purchase.total_paid)}</TableCell>
        <TableCell>{formatCurrency(purchase.total_remained_amount)}</TableCell>
        <TableCell>{purchase.purchase_items?.length || 0}</TableCell>
        <TableCell>
          <IconButton
            onClick={() => onEdit(purchase)}
            size="small"
            sx={{ mr: 1 }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => onPrint(purchase)}
            size="small"
            sx={{ mr: 1 }}
            color="primary"
          >
            <PrintIcon />
          </IconButton>
          <IconButton
            onClick={() => onDelete(purchase.id)}
            size="small"
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("purchase.purchaseItems")}
              </Typography>
              {renderItemsTable()}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const PurchaseIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [searchTerm, setSearchTerm] = useState("");
  const [filterShop, setFilterShop] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const { data: shopsFilterData, loading: shopFilterLoading } = useFetchObjects(
    ["shop"],
    "shop/shop?page_size=all",
    token
  );
  const { data: suppliersFilterData, loading: supplierFilterLoading } =
    useFetchObjects(["supplier"], "shop/suppliers?page_size=all", token);

  const shopsFilter = Array.isArray(shopsFilterData)
    ? shopsFilterData
    : shopsFilterData?.results || [];
  const suppliersFilter = Array.isArray(suppliersFilterData)
    ? suppliersFilterData
    : suppliersFilterData?.results || [];
  const branchSuppliersFilter = useMemo(
    () => suppliersFilter.filter((item) => belongsToShop(item, filterShop)),
    [suppliersFilter, filterShop]
  );

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["purchase"],
    `shop/purchases/?search=${searchTerm}&shop=${filterShop}&supplier=${filterSupplier}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("shop/purchases", token);

  useEffect(() => {
    refetch();
  }, [page, rowsPerPage, filterShop, filterSupplier, searchTerm, refetch]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleFilterShopChange = (e) => {
    const value = e.target.value;
    setFilterShop(value);
    setFilterSupplier("");
    setPage(1);
  };

  const handleFilterSupplierChange = (e) => {
    setFilterSupplier(e.target.value);
    setPage(1);
  };

  const handlePrint = (purchase) => {
    printPurchase(purchase);
  };

  const purchases = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : purchases.length;

  if (isError) {
    return <ErrorPage message={t("purchase.failedToLoadPurchases")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("purchase.purchaseList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("purchase.addNewPurchase")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("purchase.dataLoadedOn")} {formatJalaliDate()}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("purchase.searchPurchases")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          label={t("purchase.selectShop")}
          value={filterShop}
          onChange={handleFilterShopChange}
          disabled={shopFilterLoading}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">{t("purchase.allShops")}</MenuItem>
          {(shopsFilter || []).map((s) => (
            <MenuItem key={s.id} value={String(s.id)}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={t("purchase.selectSupplier")}
          value={filterSupplier}
          onChange={handleFilterSupplierChange}
          disabled={supplierFilterLoading}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">{t("purchase.allSuppliers")}</MenuItem>
          {(branchSuppliersFilter || []).map((sup) => (
            <MenuItem key={sup.id} value={String(sup.id)}>
              {sup.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="purchase table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("purchase.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.shop")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.supplier")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.purchaseDate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.discount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.totalPrice")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.totalPaid")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.totalRemained")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.itemCount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Branch",
                      "Supplier",
                      "Purchase Date",
                      "Discount",
                      "Total Price",
                      "Total Paid",
                      "Total Remained",
                      "Item Count",
                      "Action",
                    ].map((col, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : purchases?.map((purchase, i) => (
                  <PurchaseRow
                    key={purchase.id}
                    purchase={purchase}
                    index={(page - 1) * rowsPerPage + i + 1}
                    onEdit={(data) => {
                      setEditData(data);
                      setOpenEdit(true);
                    }}
                    onPrint={handlePrint}
                    onDelete={(id) => handleDelete(id)}
                  />
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center">
          <Typography>{t("hr.rowsPerPage", "Rows per page")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(e.target.value)}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
        />
      </Grid>

      {openAdd && (
        <PurchaseAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}
      {openEdit && (
        <PurchaseEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}
      <ConfirmDialog />
    </Box>
  );
};

export default PurchaseIndex;
